import Styles from './ToggleList.module.scss'
import {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  MutableRefObject,
  ReactNode,
} from 'react'
import cx from 'classnames'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import { ReactComponent as ChevronIcon } from 'common/Prose/nodeViews/ToggleList/Icons/chevron_down.svg'
import { usePress } from '@react-aria/interactions'
import { toggleStateStore } from 'common/Prose/nodeViews/ToggleList/store'
import { TextSelection } from 'prosemirror-state'
import useResizeObserver from 'use-resize-observer'

export const ToggleList = forwardRef<HTMLDivElement, { children?: ReactNode }>((props, ref) => {
  const context = useReactNodeView()
  const { node, nodeView, getPos, view } = context
  const [expanded, setExpanded] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const isSelected = context.isSelected ?? false
  const [summaryHeight, setSummaryHeight] = useState(32)

  // On every render, update the summaryHeight state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = (ref as MutableRefObject<HTMLDivElement>).current
    if (el == null) return
    const summaryEl = el.querySelector('summary')
    if (summaryEl == null) return
    setSummaryHeight(summaryEl.getBoundingClientRect().height)
  })
  // Detect size changes and re-render. Without this observer we miss edits to the summary.
  useResizeObserver({ ref: ref as MutableRefObject<HTMLDivElement> })

  useEffect(() => {
    nodeView.ignoreMutation = (mutation) => {
      // 'selection' needs to be handled, otherwise "double-click and drag" selections don't work.
      return mutation.type !== 'selection'
    }
    nodeView.setSelection = (anchor: number, head: number, root: Document) => {
      const { doc, schema } = view.state
      const $maybeToggleNodePos = doc.resolve(getPos() + anchor)
      const maybeToggleNode = $maybeToggleNodePos.node()
      const anchorDom = view.domAtPos(getPos() + anchor)
      const anchorDomEl =
        anchorDom.node.nodeType === anchorDom.node.ELEMENT_NODE
          ? (anchorDom.node as HTMLElement)
          : anchorDom.node.parentElement

      if (
        anchorDomEl != null &&
        (anchorDomEl.offsetParent == null || maybeToggleNode.type === schema.nodes.toggle_list)
      ) {
        toggleStateStore.setState(node.attrs.blockId, true)
      }

      originalSetSelection(anchor, head, root)
    }
    const originalSetSelection = (anchor: number, head: number, root: Document) => {
      const storeSetSelection = nodeView.setSelection
      nodeView.setSelection = null
      ;((view as any).docView as any).setSelection(anchor + getPos() + 1, head + getPos() + 1, root)
      nodeView.setSelection = storeSetSelection
    }
  }, [nodeView, getPos, view, node.attrs.blockId])

  useEffect(() => {
    toggleStateStore.getState(node.attrs.blockId).then((newState) => {
      if (!initialized) {
        setInitialized(true)
        setExpanded(newState)
      }
    })

    const listener = (uuid: string, newState: boolean) => {
      if (uuid === node.attrs.blockId) {
        setInitialized(true)
        setExpanded(newState)
      }
    }
    return toggleStateStore.addListener(listener)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.attrs.blockId])

  useEffect(() => {
    if (expanded) return
    // The toggle item has collapsed. Move the caret to the end of summary if it's in the body.
    const {
      state: { tr, selection, doc, schema },
      dispatch,
    } = view
    const $pos = doc.resolve(getPos() + 2)
    if ($pos.node().type !== schema.nodes.summary || $pos.node(-1).type !== schema.nodes.toggle_list) {
      return
    }
    const toggleEnd = $pos.end(-1)
    const summaryEnd = $pos.end() - 1

    const isAnchorInside = selection.anchor > summaryEnd && selection.anchor <= toggleEnd
    const isHeadInside = selection.head > summaryEnd && selection.head <= toggleEnd
    if (isAnchorInside || !isHeadInside) {
      dispatch(tr.setSelection(TextSelection.create(doc, summaryEnd)))
    }
  }, [expanded, getPos, view])

  const onToggle = useCallback(() => {
    toggleStateStore.setState(node.attrs.blockId, !expanded)
  }, [expanded, node.attrs.blockId])

  const { pressProps } = usePress({ onPress: onToggle, preventFocusOnPress: true })

  return (
    <div
      ref={ref}
      className={cx(Styles.toggleList, {
        [Styles.expanded]: expanded,
        [Styles.selected]: isSelected,
        [Styles.initialized]: initialized,
      })}
      aria-expanded={expanded}
      data-blockid={node.attrs.blockId}
    >
      <div className={Styles.leftEdge} style={{ '--summary-height': summaryHeight } as CSSProperties}>
        <ChevronIcon className={Styles.chevron} {...(pressProps as any)} />
      </div>
      {props.children}
    </div>
  )
})
